// Date-range model used on the wire and in API calls.
//
// `from` and `to` are ISO 8601 UTC strings (always ending in `Z`).
// `from` is inclusive, `to` is exclusive: rows where `DateCreated >= from AND DateCreated < to`.
//
// Presets compute their boundaries in the browser's local timezone so that
// "Today" means the user's calendar day, then convert to UTC at the boundary.

export interface DateRange {
  from: string;
  to: string;
}

export type PresetKey = "lastHour" | "today" | "yesterday";

export interface PresetDef {
  key: PresetKey;
  label: string;
  compute: (now?: Date) => DateRange;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isoUtc(d: Date): string {
  return d.toISOString();
}

export const PRESETS: PresetDef[] = [
  {
    key: "lastHour",
    label: "Last Hour",
    compute: (now = new Date()) => ({
      from: isoUtc(new Date(now.getTime() - 60 * 60 * 1000)),
      to: isoUtc(now),
    }),
  },
  {
    key: "today",
    label: "Today",
    compute: (now = new Date()) => ({
      from: isoUtc(startOfLocalDay(now)),
      to: isoUtc(now),
    }),
  },
  {
    key: "yesterday",
    label: "Yesterday",
    compute: (now = new Date()) => ({
      from: isoUtc(new Date(startOfLocalDay(now).getTime() - 24 * 60 * 60 * 1000)),
      to: isoUtc(startOfLocalDay(now)),
    }),
  },
];

export const PRESET_BY_KEY: Record<PresetKey, PresetDef> = PRESETS.reduce(
  (acc, p) => {
    acc[p.key] = p;
    return acc;
  },
  {} as Record<PresetKey, PresetDef>
);

export function dateInputToRange(fromYmd: string, toYmd: string): DateRange {
  const [fy, fm, fd] = fromYmd.split("-").map(Number);
  const [ty, tm, td] = toYmd.split("-").map(Number);
  const fromLocal = new Date(fy, fm - 1, fd);
  const toLocal = new Date(ty, tm - 1, td + 1);
  return { from: isoUtc(fromLocal), to: isoUtc(toLocal) };
}

export function defaultDateRange(): DateRange {
  return PRESET_BY_KEY.today.compute();
}

/**
 * Recompute a preset's range against the current clock. Use this on every
 * Load click so relative ranges (Last Hour, Today, Yesterday) don't go stale
 * while the page sits open. Returns the input unchanged when no preset is active.
 */
export function freshenRangeIfPreset(
  range: DateRange,
  presetKey: PresetKey | null
): DateRange {
  if (!presetKey) return range;
  const preset = PRESET_BY_KEY[presetKey];
  if (!preset) return range;
  return preset.compute();
}
